import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'
import apiService from '../services/api.service'
import { refreshAccessToken } from '../services/api.service'
import SessionExpiryModal from '../components/ui/SessionExpiryModal'
import {
  getTabId,
  broadcast as authBroadcast,
  subscribe as authSubscribe,
  awaitLeadership,
  close as closeAuthChannel,
} from '../utils/authChannel'
import { clearExpiredCookies, hasAuthTokens } from '../utils/sessionManager'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'

const ClientContext = createContext(null)

const getTenantFromURL = () => window.location.pathname.split('/').filter(Boolean)[0] || ''

const CLIENT_COOKIE_NAME = 'ow-c'
const CLIENT_LOCALSTORAGE_KEY = 'c'
const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes
const SESSION_EXPIRY_CHECK_INTERVAL = 5 * 1000 // Check for session expiry every 5 seconds

const mapClientPayload = (payload = {}) => ({
  clientId: payload.clientid ?? null,
  email: payload.emailaddress ?? null,
  displayEmail: payload.displayemail ?? null,
  externalUserId: payload.externaluserid ?? null,
  visaClientId: payload.visaclientid ?? null,
  isEnrolled: payload.isenrolled === 1,
  isContacted: payload.iscontacted === 1,
  domain: payload.domain ?? null,
  tenantId: payload.tenantid ?? null,
  tenantUrlName: payload.tenanturlname ?? null,
  languageId: payload.defaultlanguageid ?? null,
  languageCode: payload.languagecode ?? null,
  isFirstTimeLogin: payload.isFirstTimeLogin === true,
  emailVerified: payload.email_verified === true,
  cookiePreference: payload.cookiepreference ?? null,
  sessionId: payload.sessionid ?? null,
  sessionStart: payload.sessionstart ?? null,
})

// Cookie helper functions - Only for reading cookies set by backend
const getCookie = (name) => {
  const nameEQ = name + '='
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i]
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length)
    if (cookie.indexOf(nameEQ) === 0) {
      let value = decodeURIComponent(
        cookie.substring(nameEQ.length, cookie.length),
      )
      // Strip Express.js cookie-parser JSON prefix (j:) if present
      if (value.startsWith('j:')) {
        value = value.substring(2)
      }
      return value
    }
  }
  return null
}

export const ClientProvider = ({ children }) => {
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [showExpiryModal, setShowExpiryModal] = useState(false)
  const checkIntervalRef = useRef(null)
  const expiryCheckIntervalRef = useRef(null)
  const isRefreshingRef = useRef(false)
  const isSessionCheckRef = useRef(false)
  const isExpiringSessionRef = useRef(false)
  const redirectTimeoutRef = useRef(null)
  const navigate = useNavigate()

  // Sync client state with localStorage
  useEffect(() => {
    if (client) {
      localStorage.setItem(CLIENT_LOCALSTORAGE_KEY, JSON.stringify(client))
    } else {
      localStorage.removeItem(CLIENT_LOCALSTORAGE_KEY)
    }
  }, [client])

  const clearSessionIntervals = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current)
      checkIntervalRef.current = null
    }
    if (expiryCheckIntervalRef.current) {
      clearInterval(expiryCheckIntervalRef.current)
      expiryCheckIntervalRef.current = null
    }
  }, [])

  const expireSession = useCallback(({
    tenantUrl,
    broadcast = true,
    showModal = true,
    reason = 'session_expired',
  } = {}) => {
    if (isExpiringSessionRef.current) return

    isExpiringSessionRef.current = true
    clearSessionIntervals()
    clearExpiredCookies()

    const resolvedTenant =
      tenantUrl || getTenantFromURL() || client?.tenantUrlName || 'osaka'

    setClient(null)
    setIsAuthenticated(false)
    setError(null)
    localStorage.removeItem(CLIENT_LOCALSTORAGE_KEY)

    if (showModal) {
      setShowExpiryModal(true)
    }

    if (broadcast) {
      authBroadcast('LOGGED_OUT', { reason })
    }

    redirectTimeoutRef.current = setTimeout(() => {
      setShowExpiryModal(false)
      navigate(`/${resolvedTenant}/signin?reason=${reason}`, { replace: true })
      isExpiringSessionRef.current = false
      redirectTimeoutRef.current = null
    }, showModal ? 3000 : 0)
  }, [clearSessionIntervals, client?.tenantUrlName, navigate])

  const clearAuthenticatedState = useCallback(() => {
    clearSessionIntervals()
    setClient(null)
    setIsAuthenticated(false)
    setError(null)
    localStorage.removeItem(CLIENT_LOCALSTORAGE_KEY)
  }, [clearSessionIntervals])

  // Refresh token function — delegates to the shared promise in api.service.js
  // so that concurrent callers (interceptor, checkSession) share one in-flight request.
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      // Another refresh is already in progress via this context;
      // still return the shared promise result
      try {
        const response = await refreshAccessToken()
        return !!(response && response.clientid)
      } catch {
        return false
      }
    }

    isRefreshingRef.current = true

    try {
      const response = await refreshAccessToken()

      if (response && response.clientid) {
        const mapped = mapClientPayload(response)
        setClient(mapped)
        setIsAuthenticated(true)
        isExpiringSessionRef.current = false
        return true
      } else {
        clearAuthenticatedState()
        return false
      }
    } catch (error) {
      logger.error('ClientContext - Token refresh error:', sanitizeLogMessage(error.message))
      if (!error.response && !error.status) {
        return null
      }
      clearAuthenticatedState()
      return false
    } finally {
      isRefreshingRef.current = false
    }
  }, [clearAuthenticatedState])

  // Check session with backend
  const checkSession = useCallback(async () => {
    if (isSessionCheckRef.current) return null

    isSessionCheckRef.current = true
    try {
      const response = await apiService.get('/api/kc/check-session')

      if (response && response.authenticated) {
        const mapped = mapClientPayload(response)
        setClient(mapped)
        setIsAuthenticated(true)
        isExpiringSessionRef.current = false
        // Backend sets the cookie
        return true
      } else {
        clearAuthenticatedState()
        // Backend handles cookie deletion
        return false
      }
    } catch (error) {
      logger.error('ClientContext - Session check error:', sanitizeLogMessage(error.message))

      // Check both error.data (from apiService.handleError) and error.response.data (direct axios)
      const errorData = error.data || error.response?.data
      if (errorData?.needsRefresh || errorData?.code === 'TOKEN_EXPIRED') {
        return await refreshToken()
      }

      const status = error.status || error.response?.status
      if (!status || status >= 500) {
        return null
      }

      // Session invalid
      clearAuthenticatedState()
      // Backend handles cookie deletion
      return false
    } finally {
      isSessionCheckRef.current = false
    }
  }, [clearAuthenticatedState, refreshToken])

  // Load client data from cookie/localStorage on mount and start session checking
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First, check if we have cookie or localStorage data
        let storedClient = getCookie(CLIENT_COOKIE_NAME)
        if (!storedClient) {
          const localClient = localStorage.getItem(CLIENT_LOCALSTORAGE_KEY)
          if (localClient) {
            storedClient = localClient
          }
        }

        if (storedClient) {
          const parsed = JSON.parse(storedClient)

          // Check if cookie belongs to the current URL tenant
          const urlTenant = getTenantFromURL()
          if (parsed.tenanturlname && urlTenant && parsed.tenanturlname !== urlTenant) {
            // Cookie is for a different tenant — don't authenticate
            setClient(null)
            setIsAuthenticated(false)
            setLoading(false)
            return
          }

          setClient(parsed)
          setIsAuthenticated(true)

          // Only verify with backend if we have a stored session
          const isValid = await checkSession()
          if (isValid === false) {
            clearAuthenticatedState()
          }
        }
      } catch (e) {
        logger.error('ClientContext - Failed to initialize auth:', sanitizeLogMessage(e))
        clearAuthenticatedState()
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [checkSession, clearAuthenticatedState])

  // Start/stop periodic session checking — only the elected leader tab polls.
  useEffect(() => {
    if (!isAuthenticated) {
      clearSessionIntervals()
      return
    }

    let cancelled = false

    const startLeaderPolling = async () => {
      try {
        await awaitLeadership()
      } catch {
        // Channel closed before leadership resolved
        return
      }
      if (cancelled) return

      checkIntervalRef.current = setInterval(() => {
        checkSession()
      }, TOKEN_CHECK_INTERVAL)
    }

    startLeaderPolling()

    return () => {
      cancelled = true
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
    }
  }, [isAuthenticated, checkSession, clearSessionIntervals])

  const hydrateFromSignIn = useCallback((payload) => {
    try {
      setLoading(true)
      const mapped = mapClientPayload(payload)

      // Save to state only - Backend sets the cookie
      setClient(mapped)
      setIsAuthenticated(true)
      isExpiringSessionRef.current = false
      setError(null)

      // Notify other tabs about the new session
      authBroadcast('ACCOUNT_SWITCHED', { clientId: mapped.clientId })
    } catch (e) {
      setError(e?.message || 'Failed to set client')
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = async (tenantId = null) => {
    try {
      // Call backend client signout API to clear cookie server-side
      if (client?.clientId && tenantId) {
        await apiService.post('/api/clients/clientsignout', {
          clientid: client.clientId,
          tenantid: tenantId,
        })
      }
      clearAuthenticatedState()

      // Notify other tabs so they redirect to sign-in
      authBroadcast('LOGGED_OUT', { reason: 'logout' })
    } catch (e) {
      logger.error('Failed to logout:', sanitizeLogMessage(e))
      // Clear client state even if API call fails
      clearAuthenticatedState()
      setError(e?.message || 'Failed to logout')

      // Still notify other tabs even on error
      authBroadcast('LOGGED_OUT', { reason: 'logout' })
    }
  }

  const updateClient = useCallback((updates) => {
    setClient((prev) => ({ ...prev, ...updates }))
  }, [])

  // ===== SESSION EXPIRY MONITORING =====
  // Session expiry monitoring - checks if ow-c cookie exists
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear expiry check interval when not authenticated
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current)
        expiryCheckIntervalRef.current = null
      }
      return
    }

    // Add a small delay to ensure cookie is set by backend before starting monitoring
    const startMonitoringTimeout = setTimeout(() => {
      // Verify cookie exists before starting interval
      const initialCookie = getCookie(CLIENT_COOKIE_NAME)
      if (!initialCookie) {
        // Don't start monitoring if cookie doesn't exist
        return
      }

      // Check for session expiry every 5 seconds
      expiryCheckIntervalRef.current = setInterval(async () => {
        const owCookie = getCookie(CLIENT_COOKIE_NAME)

        // Check tenant mismatch (user navigated to different tenant URL in same tab)
        if (owCookie && isAuthenticated) {
          try {
            const parsed = JSON.parse(owCookie)
            const urlTenant = getTenantFromURL()
            if (parsed.tenanturlname && urlTenant && parsed.tenanturlname !== urlTenant) {
              clearInterval(expiryCheckIntervalRef.current)
              expiryCheckIntervalRef.current = null
              setClient(null)
              setIsAuthenticated(false)
              localStorage.removeItem(CLIENT_LOCALSTORAGE_KEY)
              return
            }
          } catch (e) { /* ignore parse errors */ }
        }

        if (!owCookie && isAuthenticated) {
          const recovered = await checkSession()
          if (recovered === true || recovered === null || hasAuthTokens()) {
            return
          }

          expireSession({ reason: 'session_expired' })
        }
      }, SESSION_EXPIRY_CHECK_INTERVAL)
    }, 2000) // Wait 2 seconds for cookie to be set by backend

    // Cleanup on unmount or when auth changes
    return () => {
      clearTimeout(startMonitoringTimeout) // Clear the initial timeout
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current)
        expiryCheckIntervalRef.current = null
      }
    }
  }, [isAuthenticated, checkSession, expireSession])
  // ===== END SESSION EXPIRY MONITORING =====

  // ===== INTERCEPTOR SESSION EXPIRY HANDLER =====
  // Listen for session-expired events dispatched by the API interceptor
  useEffect(() => {
    const handleSessionExpiredEvent = (e) => {
      expireSession({
        tenantUrl: e.detail?.tenantUrl,
        reason: e.detail?.reason || 'session_expired',
      })
    }

    window.addEventListener('session-expired', handleSessionExpiredEvent)
    return () => window.removeEventListener('session-expired', handleSessionExpiredEvent)
  }, [expireSession])

  // ===== CROSS-TAB AUTH SYNC VIA BROADCAST CHANNEL =====
  useEffect(() => {
    const myTabId = getTabId()

    const unsubscribe = authSubscribe((msg) => {
      // Ignore messages originating from this tab
      if (msg.tabId === myTabId) return

      switch (msg.type) {
        case 'ACCOUNT_SWITCHED':
          // Another tab signed in — silently re-hydrate from the server
          checkSession()
          break

        case 'LOGGED_OUT': {
          expireSession({
            broadcast: false,
            showModal: msg.reason !== 'logout',
            reason: msg.reason === 'logout' ? 'logout' : 'session_expired',
          })
          break
        }

        default:
          break
      }
    })

    return () => {
      unsubscribe()
    }
  }, [checkSession, expireSession])

  // Clean up broadcast channel on unmount
  useEffect(() => {
    return () => {
      clearSessionIntervals()
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
      closeAuthChannel()
    }
  }, [clearSessionIntervals])
  // ===== END CROSS-TAB AUTH SYNC =====

  const value = {
    client,
    loading,
    error,
    isAuthenticated,
    hydrateFromSignIn,
    logout,
    checkSession,
    refreshToken,
    updateClient,
  }

  return (
    <ClientContext.Provider value={value}>
      {children}
      {showExpiryModal && <SessionExpiryModal />}
    </ClientContext.Provider>
  )
}

export const useClient = () => {
  const ctx = useContext(ClientContext)
  if (!ctx) throw new Error('useClient must be used within ClientProvider')
  return ctx
}
